import { setCookie } from 'cookies-next/client';

import { getCurrencyCodeFromCountryCode } from '../currencies/helpers';

import {
  type GetFilterAggregatesQueryParams,
  type SearchProductsRanking,
} from './aliases';
import { ITEMS_PER_PAGE, TRENDING_SEARCHES_SUGGESTIONS } from './constants';
import {
  type SearchSuggestionOption,
  SearchFrom,
  type SearchSortByValues,
  ShopSortByValues,
  isSearchSort,
  isShopSort,
} from './types';
import { type SearchFilters } from './useSearchFilters';

import { RECENT_SEARCH_VAL } from '@/constants/cookies.ts';
import { CATEGORIES_ENDPOINT } from '@/constants/endpoints.ts';
import {
  BOOSTED_GROUP_SIZE,
  BOOSTED_SPACING,
} from '@/modules/boostedListings/constants';
import { type LegacyCategory } from '@/modules/categories/types';
import { normaliseCompositeSizeIds } from '@/modules/filters/helpers';
import http from '@/modules/http';
import { type AxiosCompatibleResponse } from '@/modules/http/types.ts';
import { SupportedLocation } from '@/modules/location/constants';
import { type YouMightBeIntoLocalStorageItem } from '@/modules/product/types';
import { SearchUrlController } from '@/modules/search/SearchUrlController';
import {
  type GetShopFilterAggregatesQueryParams,
  type GetShopProductsQueryParams,
} from '@/modules/shop/aliases';
import { SHOP_PAGE_SIZE } from '@/modules/shop/constants';
import { getLocalStorageItem, setLocalStorageItem } from '@/modules/storage';
import { safeJsonParse } from '@/modules/storage/helpers';

const RECENT_SEARCH_KEY = 'recentSearchHistory';

export function getSearchHistory() {
  const recentSearch = getLocalStorageItem(RECENT_SEARCH_KEY);
  return safeJsonParse(recentSearch, []);
}

export function setSearchHistory(history: SearchSuggestionOption[]) {
  setCookie(RECENT_SEARCH_VAL, history[0]?.value || '');
  return setLocalStorageItem(RECENT_SEARCH_KEY, JSON.stringify(history));
}

const GENERIC_MW_WW_GROUPS = [
  'tops',
  'bottoms',
  'coats-jackets',
  'footwear',
  'jumpsuit-and-playsuit',
  'dresses',
  'suits',
];

export const genericMenswearWomenswearGroupsStr =
  GENERIC_MW_WW_GROUPS.join(',');

const SORT_TO_RANKING: Record<SearchSortByValues, SearchProductsRanking> = {
  relevance: 'product',
  priceAscending: 'product_price_asc',
  priceDescending: 'product_price_desc',
  newlyListed: 'recent',
  popular: 'popular',
};

function toSearchRanking(sort?: SearchFilters['sort']) {
  return isSearchSort(sort) ? SORT_TO_RANKING[sort] : undefined;
}

function toFilterAggregatesRanking(
  sort?: SearchFilters['sort']
): GetFilterAggregatesQueryParams['ranking'] {
  const ranking = toSearchRanking(sort);

  return ranking === 'popular' ? undefined : ranking;
}

export function buildSearchQueryParams({
  what,
  searchFilters: filters,
  country,
  after,
  user_id,
  includeBoostedListings = false,
  limit = ITEMS_PER_PAGE,
  from = SearchFrom.InCountrySearch,
}: {
  searchFilters: SearchFilters;
  what?: string;
  country: string;
  after?: string;
  user_id?: number;
  includeBoostedListings?: boolean;
  limit?: number;
  from?: SearchFrom;
}) {
  return {
    what,
    after,
    limit,
    country,
    user_id,
    price_min: filters.priceMin ? filters.priceMin : undefined,
    price_max: filters.priceMax ? filters.priceMax : undefined,
    brands: filters.brands?.join(','),
    variants: filters.sizes?.length
      ? normaliseCompositeSizeIds(filters.sizes).join(',')
      : undefined,
    condition: filters.conditions?.join(','),
    colours: filters.colours?.join(','),
    ranking: toSearchRanking(filters.sort),
    currency: getCurrencyCodeFromCountryCode(country),
    discounted: filters.isDiscounted,
    discount_types: filters.discountTypes,
    min_discount: filters.minDiscount,
    max_discount: filters.maxDiscount,
    shipping_id: filters.shippingId,
    groups: filters.groups,
    product_types: filters.productTypes,
    is_kids: filters.isKids,
    gender: filters.gender,
    from,
    include_like_count: true,
    ...(includeBoostedListings &&
      user_id && {
        boosted_spacing: BOOSTED_SPACING,
        boosted_group_size: BOOSTED_GROUP_SIZE,
      }),
  };
}

const SHOP_RANKING_BY_SORT: Partial<
  Record<ShopSortByValues, NonNullable<GetShopProductsQueryParams['ranking']>>
> = {
  [ShopSortByValues.Product]: 'product',
  [ShopSortByValues.ProductPriceAsc]: 'product_price_asc',
  [ShopSortByValues.ProductPriceDesc]: 'product_price_desc',
  [ShopSortByValues.Recent]: 'recent',
};

const SHOP_GENDERS = [
  'male',
  'female',
  'unisex',
] as const satisfies readonly NonNullable<
  GetShopProductsQueryParams['gender']
>[];

function toShopRanking(
  sort?: SearchFilters['sort']
): GetShopProductsQueryParams['ranking'] {
  return isShopSort(sort) ? SHOP_RANKING_BY_SORT[sort] : undefined;
}

function toShopGender(gender?: string): GetShopProductsQueryParams['gender'] {
  return SHOP_GENDERS.find((supported) => supported === gender);
}

export function buildShopSearchQueryParams({
  after,
  location,
  searchFilters: filters,
  searchInput,
  limit = SHOP_PAGE_SIZE,
}: {
  after?: string;
  limit?: number;
  location: string;
  searchFilters: SearchFilters;
  searchInput?: string;
}): GetShopProductsQueryParams {
  const hasPrice = Boolean(filters.priceMin || filters.priceMax);

  return {
    limit: String(limit),
    after: after ? after : undefined,
    what: searchInput ? searchInput : undefined,
    brands: filters.brands?.join(','),
    condition: filters.conditions?.join(','),
    colours: filters.colours?.join(','),
    groups: filters.groups,
    product_types: filters.productTypes?.join(','),
    is_kids: filters.isKids ? 'true' : undefined,
    gender: toShopGender(filters.gender),
    discounted: filters.isDiscounted ? 'true' : undefined,
    price_min: filters.priceMin ? String(filters.priceMin) : undefined,
    price_max: filters.priceMax ? String(filters.priceMax) : undefined,
    currency: hasPrice ? getCurrencyCodeFromCountryCode(location) : undefined,
    ranking: toShopRanking(filters.sort),
    variants: filters.sizes?.length
      ? normaliseCompositeSizeIds(filters.sizes).join(',')
      : undefined,
  };
}

function toBooleanParam(value?: boolean): 'true' | 'false' | undefined {
  if (value === undefined) {
    return undefined;
  }

  return value ? 'true' : 'false';
}

function buildCommonFilterAggregatesParams({
  searchInput,
  searchFilters: filters,
  location,
}: {
  searchInput: string;
  searchFilters: SearchFilters;
  location: string;
}) {
  return {
    what: searchInput,
    price_min: filters.priceMin ? String(filters.priceMin) : undefined,
    price_max: filters.priceMax ? String(filters.priceMax) : undefined,
    brands: filters.brands?.join(','),
    condition: filters.conditions?.join(','),
    colours: filters.colours?.join(','),
    currency: getCurrencyCodeFromCountryCode(location),
    discounted: toBooleanParam(filters.isDiscounted),
    groups: filters.groups,
    product_types: filters.productTypes?.join(','),
    gender: toShopGender(filters.gender),
    is_kids: toBooleanParam(filters.isKids),
  };
}

export function buildFilterAggregatesSearchQueryParams({
  searchInput,
  searchFilters,
  location,
  from,
}: {
  searchInput: string;
  searchFilters: SearchFilters;
  location: string;
  from?: SearchFrom;
}): GetFilterAggregatesQueryParams {
  return {
    ...buildCommonFilterAggregatesParams({
      searchInput,
      searchFilters,
      location,
    }),
    country: location,
    ranking: toFilterAggregatesRanking(searchFilters.sort),
    from,
  };
}

// A shop's product list is not country-filtered, so its aggregates must not be
// either. Otherwise viewers in a country with no matching products see empty
// filter facets.
export function buildShopFilterAggregatesSearchQueryParams(params: {
  searchInput: string;
  searchFilters: SearchFilters;
  location: string;
}): GetShopFilterAggregatesQueryParams {
  return buildCommonFilterAggregatesParams(params);
}

export function sanitiseSearchQuery(query: string) {
  return encodeURIComponent(query.replace(/-/g, ' '));
}

export function getActiveFiltersCount(filters: SearchFilters) {
  const { sort, ...restFilters } = filters;

  return Object.values(restFilters)
    .filter(Boolean)
    .reduce<number>((sum, value) => {
      if (Array.isArray(value)) {
        return sum + value.length;
      }
      return sum + 1;
    }, 0);
}

/**
 * Convert query params object to either a string with a leading `?`
 * or an empty string
 */
export function formatQueryParams(queryParams: URLSearchParams) {
  const queryParamsString = queryParams.toString();
  if (!queryParamsString.length) {
    return '';
  }
  return `?${queryParamsString}`;
}

const brandAndCategoryRegex =
  /(\/brands\/([^\/]+)\/)|(\/(?:category|c)\/(.*)\/)|(\/explore\/($|\?.*))/;

export function getIsPathParamsMode(pathname: string) {
  return brandAndCategoryRegex.test(pathname);
}

export function fetchFullCategoryTree(): Promise<
  AxiosCompatibleResponse<LegacyCategory[]>
> {
  return http.get(CATEGORIES_ENDPOINT, { cache: 'force-cache' });
}

/**
 * Used to create a list of unique brand ids from a list of branded category pills
 * @param brandCategoryList
 */
export function getUniqueBrandIds(
  brandCategoryList: YouMightBeIntoLocalStorageItem[]
) {
  const uniqueBrandIdsSet = brandCategoryList.reduce((uniqueIds, item) => {
    return uniqueIds.add(String(item.brandId));
  }, new Set<string>());
  return Array.from(uniqueBrandIdsSet);
}

export function youMightBeIntoPillHref({
  brandId,
  group,
  productType,
  searchQuery,
  gender,
  isKids,
}: {
  brandId: number;
  group: string;
  gender?: string;
  isKids: boolean;
  productType?: string;
  searchQuery: string;
}) {
  const urlController = new SearchUrlController();
  const { searchParams } = urlController.toUrl(
    {
      activeGroup: group,
      activeProductTypes: productType ? [productType] : undefined,
      activeBrandIds: [brandId],
      activeGender: gender,
      isKids,
    },
    searchQuery,
    true
  );
  const searchParamsString = new URLSearchParams(searchParams).toString();

  return `/search/?${searchParamsString}`;
}

export function getTrendingSearchSuggestions(
  location: SupportedLocation
): SearchSuggestionOption[] {
  return (
    TRENDING_SEARCHES_SUGGESTIONS[location] ||
    TRENDING_SEARCHES_SUGGESTIONS[SupportedLocation.US]
  );
}
